import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useSelector, useDispatch } from 'react-redux';
import { SidebarProvider } from "@/components/ui/sidebar";
import { db } from '../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { setUser } from '../State/usersSlice';

// Headers
import Header from "./Header";
import TeacherHeader from "../Teacherdashboard/Teacherheader";
import GuardianHeader from "../Parentdashboard/GuardianHeader";
import StudentHeader from "../Studentfile/StudentHeader";

// Sidebars
import AdminSidebar from "./Sidebar";
import TeacherSidebar from "../Teacherdashboard/Teachersidebar";
import GuardianSidebar from "../Parentdashboard/GuardianSidebar";
import StudentSidebar from "../Studentfile/StudentSidebar";

const SubscriptionExpired = () => {
  // merry christmass
  const navigate = useNavigate();
  const currentUser = useSelector((state: any) => state.users?.currentUser);
  const dispatch = useDispatch();
  const role = currentUser?.selectedAffiliation?.role;
  const subscriptionStatus = currentUser?.subscriptionStatus;
  const schoolID = currentUser?.selectedAffiliation?.schoolID;

  const subscriptionExpiry = currentUser?.subscriptionExpiry;
  const isExpiredByDate = subscriptionExpiry ? Date.now() > subscriptionExpiry : false;

  const isSchoolAdmin = role === 'admin';

  // Auto-redirect if subscription becomes active AND is not expired by date
  useEffect(() => {
    // REAL-TIME LISTENER: Detect payment confirmation from other devices/users
    if (!schoolID || schoolID === 'null') return;

    const schoolRef = doc(db, `schools/${schoolID}/schoolDetails/main`);
    
    // merry christmass
    const unsubscribe = onSnapshot(schoolRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const newStatus = data.subscriptionStatus;
        const newExpiry = data.subscriptionExpiry?.toMillis() || 0;
        const now = Date.now();
        const isActuallyActive = (newStatus === 'active' || newStatus === 'trial') && (newExpiry > now);

        if (isActuallyActive && subscriptionStatus !== newStatus) {
           console.log(`SubscriptionExpired - Detected status change to ${newStatus}. Restoring access...`);
           
           // Update Redux so App.tsx and other components know we are back
           dispatch(setUser({
             ...currentUser,
             subscriptionStatus: newStatus,
             subscriptionExpiry: newExpiry
           }));

           // Small delay to ensure state propagates, then HARD RELOAD to dashboard
           setTimeout(() => {
             let target = '/dashboard';
             switch (role) {
               case 'admin': target = '/admin-dashboard'; break;
               case 'school_staff': target = '/faculty-dashboard'; break;
               case 'guardian': target = '/guardian-dashboard'; break;
               case 'student': target = '/student-dashboard'; break;
             }
             window.location.href = target;
           }, 1000);
        }
      }
    });

    return () => unsubscribe();
  }, [schoolID, subscriptionStatus, role, navigate, dispatch, currentUser]);

  // If status is active and NOT expired, show loader while redirecting
  if ((subscriptionStatus === 'active' || subscriptionStatus === 'trial') && !isExpiredByDate) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Helper to render the correct sidebar based on role
  const renderSidebar = () => {
    if (!role) return null; // Don't show admin sidebar by default if role is missing/loading
    
    switch (role) {
      case 'admin':
        return <AdminSidebar onSectionChange={() => {}} />;
      case 'school_staff':
        return <TeacherSidebar />;
      case 'guardian':
        return <GuardianSidebar />;
      case 'student':
        return <StudentSidebar />;
      default:
        // Safest fallback is nothing until we know the role
        return null; 
    }
  };

  // Helper to render the correct header based on role
  const renderHeader = () => {
    if (!role) return null;

    switch (role) {
      case 'admin':
        return <Header />;
      case 'school_staff':
        return <TeacherHeader />;
      case 'guardian':
        return <GuardianHeader />;
      case 'student':
        return <StudentHeader />;
      default:
        return <Header />;
    }
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        {renderSidebar()}
        <div className="flex flex-col flex-1 h-full">
          {renderHeader()}
          <main className="flex-1 overflow-y-auto p-3 sm:p-6 flex items-center justify-center">
            <Card className="w-full max-w-md text-center shadow-lg border-red-200 dark:border-red-900">
              <CardHeader>
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-red-100 rounded-full animate-pulse">
                    <AlertTriangle className="h-8 w-8 text-red-600" />
                  </div>
                </div>
                <CardTitle className="text-2xl text-red-600">Access Restricted</CardTitle>
                <CardDescription className="text-lg mt-2">
                  Your school's subscription is currently <strong>{subscriptionStatus || 'inactive'}</strong>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {isSchoolAdmin 
                    ? "Please renew your subscription to restore access for all users."
                    : "Please contact your school administrator to resolve this issue."}
                </p>
                
                {isSchoolAdmin ? (
                  <Button 
                    className="w-full bg-red-600 hover:bg-red-700 text-white" 
                    onClick={() => navigate('/dashboard/subscription')}
                  >
                    Manage Subscription
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate('/dashboard')}
                  >
                    Back to Dashboard
                  </Button>
                )}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                  <details className="text-left">
                    <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 transition-colors">
                      Show Diagnostic Data
                    </summary>
                    <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded text-[10px] font-mono overflow-auto max-h-40 space-y-1">
                      <p><span className="text-blue-500">UID:</span> {currentUser?.id || 'N/A'}</p>
                      <p><span className="text-blue-500">Role:</span> {role || 'N/A'}</p>
                      <p><span className="text-blue-500">Status:</span> {subscriptionStatus || 'undefined'}</p>
                      <p><span className="text-blue-500">Expiry:</span> {currentUser?.subscriptionExpiry ? new Date(currentUser.subscriptionExpiry).toLocaleString() : 'N/A'}</p>
                      <p><span className="text-blue-500">SchoolID:</span> {currentUser?.selectedAffiliation?.schoolID || 'N/A'}</p>
                      <p><span className="text-blue-500">ExpiredByDate:</span> {isExpiredByDate ? 'Yes' : 'No'}</p>
                      <p><span className="text-blue-500">Timestamp:</span> {new Date().toISOString()}</p>
                    </div>
                  </details>
                </div>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default SubscriptionExpired;
